import React, { useContext, useState, useEffect } from 'react';
import axios from '../api/http';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { AuthContext } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';

// Farben für die Diagramme
const COLORS = ['#1976d2', '#dc004e', '#4caf50', '#ff9800', '#9c27b0', '#00acc1'];

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>({
    totalHoursThisWeek: 0,
    projectCount: 0,
    lastEntry: null,
    weeklyTimeData: [],
    projectTimeData: []
  });

  // Laden der Dashboard-Daten vom Backend
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get('/api/dashboard');
        setData(response.data);
      } catch (err: any) {
        console.error('Fehler beim Laden der Dashboard-Daten:', err);
        setError(err.response?.data?.message || 'Fehler beim Laden der Dashboard-Daten');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Formatiere Datum für die Anzeige
  const formatDate = (dateStr: string) => {
    try {
      const date = parseISO(dateStr);
      return format(date, 'dd.MM.yyyy');
    } catch (error) {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" paragraph>
        Willkommen zurück, {user?.name || 'Benutzer'}!
      </Typography>

      {/* Statistik-Karten */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Gesamtstunden diese Woche
            </Typography>
            <Typography variant="h3" color="primary">
              {parseFloat(data.totalHoursThisWeek).toFixed(2)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Aktive Projekte
            </Typography>
            <Typography variant="h3" color="secondary">
              {data.projectCount}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Letzte Erfassung
            </Typography>
            {data.lastEntry ? (
              <>
                <Typography variant="h5">
                  {formatDate(data.lastEntry.date)}
                </Typography>
                <Typography variant="body1">
                  Projekt: {data.lastEntry.project_name}
                </Typography>
              </>
            ) : (
              <Typography variant="body1">
                Keine Erfassungen vorhanden
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Diagramme */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Wochenstunden" />
            <Divider />
            <CardContent>
              <Box sx={{ height: isMobile ? 250 : 300 }}>
                {data.weeklyTimeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.weeklyTimeData} margin={isMobile ? { top: 5, right: 10, left: -20, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: isMobile ? 12 : 14 }} />
                      <YAxis tick={{ fontSize: isMobile ? 12 : 14 }} />
                      <Tooltip formatter={(value) => parseFloat(value as string).toFixed(2)} />
                      <Legend wrapperStyle={{ fontSize: isMobile ? 12 : 14 }} />
                      <Bar dataKey="hours" fill="#1976d2" name="Stunden" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                  }}>
                    <Typography variant="body1" color="textSecondary">
                      Keine Stundendaten für diese Woche verfügbar
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Projektverteilung" />
            <Divider />
            <CardContent>
              <Box sx={{ height: isMobile ? 250 : 300 }}>
                {data.projectTimeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={isMobile ? { top: 0, right: 0, left: 0, bottom: 0 } : { top: 5, right: 30, left: 20, bottom: 5 }}>
                      <Pie
                        data={data.projectTimeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => isMobile ? `${(percent * 100).toFixed(0)}%` : `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={isMobile ? 60 : 80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {data.projectTimeData.map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => parseFloat(value as string).toFixed(2)}
                        contentStyle={{ fontSize: isMobile ? 12 : 14 }}
                      />
                      <Legend 
                        wrapperStyle={{ fontSize: isMobile ? 12 : 14 }}
                        layout={isMobile ? "horizontal" : "vertical"}
                        align={isMobile ? "center" : "right"}
                        verticalAlign={isMobile ? "bottom" : "middle"}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%'
                  }}>
                    <Typography variant="body1" color="textSecondary">
                      Keine Projektdaten verfügbar
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 